/**
 * Life4Earth V2 — Follow API adapter (DEV-1324, epic DEV-1329)
 *
 * Generic polymorphic follow: a user follows (targetType, targetGuid).
 * Unfollow = delete. Feeds the personalized feed (DEV-1330).
 */

import { apiFetch } from "./client";
import { invalidateAndRefetch } from "./queryCache";
import { invalidateOnboardingState } from "./onboarding";

export type FollowTargetType =
  | "project"
  | "mini_project"
  | "community"
  | "chronicle"
  | "user"
  | "solution"
  | "problem";

export interface FollowStatus {
  following: boolean;
  followerCount: number;
}

export interface FollowItem {
  followGuid: string;
  targetType: string;
  targetGuid: string;
  createdAt: string;
}

/** Follow a target (idempotent). */
export async function followEntity(targetType: FollowTargetType, targetGuid: string): Promise<FollowStatus> {
  const res = await apiFetch<FollowStatus>("/api/follows", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ targetType, targetGuid }),
  });
  // Following is a rung basic (≥3 follows) — the 3rd follow can trigger the
  // Explorer→Contributor promotion, and the celebration popup fires from
  // App.tsx's onboarding hook instance. Refresh follows + onboarding
  // everywhere (Skip bug report, 2026-07-08).
  invalidateAndRefetch("myFollows");
  invalidateOnboardingState();
  return res;
}

/** Unfollow a target (idempotent). */
export async function unfollowEntity(targetType: FollowTargetType, targetGuid: string): Promise<FollowStatus> {
  const res = await apiFetch<FollowStatus>(`/api/follows/${targetType}/${targetGuid}`, { method: "DELETE" });
  invalidateAndRefetch("myFollows");
  return res;
}

/** Whether the current user follows this target, plus the follower count. */
export function getFollowStatus(targetType: FollowTargetType, targetGuid: string): Promise<FollowStatus> {
  return apiFetch<FollowStatus>(`/api/follows/status/${targetType}/${targetGuid}`);
}

/** What the current user follows, optionally filtered by target type. */
export function getMyFollows(targetType?: FollowTargetType): Promise<FollowItem[]> {
  const qs = targetType ? `?targetType=${encodeURIComponent(targetType)}` : "";
  return apiFetch<FollowItem[]>(`/api/follows/mine${qs}`);
}
